import { Button, Card, Tag } from "@arco-design/web-react";
import { Pencil, Plus } from "lucide-react";

import { dicStayAreaLevelLabels, dicStayAreaPageSizeOptions } from "../config";
import type { DicStayAreaItem } from "../types";
import "./DicStayAreaPage.css";

type DicStayAreaResultTableProps = {
  canManage: boolean;
  items: DicStayAreaItem[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onCreateStayArea: () => void;
  onEditStayArea: (item: DicStayAreaItem) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

/** 缺失的必需资料和已有占位文字用红色标签提醒，选填空值保持空白。 */
function renderAreaField(value: string, required = false) {
  const text = value.trim();
  return text === "待补充" || (required && !text) ? <Tag color="red">待补充</Tag> : value;
}

/** 住宿点结果表格。 */
export function DicStayAreaResultTable({
  canManage,
  items,
  loading,
  page,
  pageSize,
  total,
  onCreateStayArea,
  onEditStayArea,
  onPageChange,
  onPageSizeChange,
}: DicStayAreaResultTableProps) {
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  return (
    <Card className="dic-stay-area-card" bodyStyle={{ padding: 0 }}>
      <div className="dic-stay-area-results">
        <div className="dic-stay-area-results-header">
          <h2>住宿点字典</h2>
          <Button shape="round" icon={<Plus size={16} />} disabled={!canManage} onClick={onCreateStayArea}>
            新增住宿点
          </Button>
        </div>
        <div className="dic-stay-area-table-scroll">
          <table className="dic-stay-area-table">
            <thead>
              <tr>
                <th>操作</th>
                <th>住宿点名称</th>
                <th>拼音码</th>
                <th>层级</th>
                <th>省</th>
                <th>市</th>
                <th>区县</th>
                <th>状态</th>
                <th>来源</th>
                <th>更新时间</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="dic-stay-area-empty">
                    正在加载住宿点...
                  </td>
                </tr>
              ) : null}
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="dic-stay-area-empty">
                    暂无住宿点数据
                  </td>
                </tr>
              ) : null}
              {!loading
                ? items.map((item) => (
                    <tr key={item.stayAreaId}>
                      <td>
                        <div className="dic-stay-area-row-actions">
                          <Button
                            type="text"
                            shape="circle"
                            aria-label={`编辑住宿点 ${item.stayAreaName}`}
                            title="编辑住宿点"
                            icon={<Pencil size={16} />}
                            disabled={!canManage}
                            onClick={() => onEditStayArea(item)}
                          />
                        </div>
                      </td>
                      <td>{item.stayAreaName}</td>
                      <td>{item.spellCode}</td>
                      <td>{renderAreaField(dicStayAreaLevelLabels[item.areaLevel] ?? item.areaLevel, true)}</td>
                      <td>{renderAreaField(item.province, true)}</td>
                      <td>{renderAreaField(item.city, true)}</td>
                      <td>{renderAreaField(item.district)}</td>
                      <td>
                        <Tag color={item.isDisabled ? "orangered" : "green"}>
                          {item.isDisabledLabel}
                        </Tag>
                      </td>
                      <td>
                        <Tag color={item.isAiCreated ? "arcoblue" : "gray"}>
                          {item.isAiCreatedLabel}
                        </Tag>
                      </td>
                      <td>{item.updatedAt}</td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
        <div className="dic-stay-area-pagination">
          <span>共 {total} 条</span>
          <Button disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            上一页
          </Button>
          <span>
            {page} / {totalPages}
          </span>
          <Button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            下一页
          </Button>
          <label className="dic-stay-area-field">
            <span>每页条数</span>
            <select
              aria-label="每页条数"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {dicStayAreaPageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option} 条
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </Card>
  );
}
